import { FavouriteMealsResponse } from "@/clients/models/FavouriteMealsResponse";
import { MealTime } from "@/clients/models/MealTime";
import { MealsService } from "@/clients/services/MealsService";
import { QueryKeys } from "@/constants/query-keys";
import { guardGetUserFromLocalStorage } from "@/utils/request";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

const mealTimeOrder: MealTime[] = [
  MealTime.BREAKFAST,
  MealTime.LUNCH,
  MealTime.DINNER,
  MealTime.SNACK,
];

export const useGetFavouriteMeals = () => {
  const { data, isLoading, error, refetch, isRefetching, isError } =
    useQuery<FavouriteMealsResponse>({
      queryKey: [QueryKeys.FAVORITE_MEALS],
      queryFn: async () => {
        const user = guardGetUserFromLocalStorage();
        const { data } = await MealsService.getFavouriteMealsApiV1MealsFavouritesGet(user.id);
        return data;
      },
    });

  const favouriteMealsByMealTime = useMemo(() => {
    return {
      [MealTime.BREAKFAST]: data?.breakfast ?? [],
      [MealTime.LUNCH]: data?.lunch ?? [],
      [MealTime.DINNER]: data?.dinner ?? [],
      [MealTime.SNACK]: data?.snack ?? [],
    };
  }, [data]);

  const favouriteMeals = useMemo(() => {
    return mealTimeOrder.flatMap((mealTime) => favouriteMealsByMealTime[mealTime] ?? []);
  }, [favouriteMealsByMealTime]);

  const favouriteMealsSet = useMemo(() => {
    const ids = favouriteMeals
      .map((meal) => meal.id ?? "")
      .filter((id): id is string => Boolean(id));
    return new Set(ids);
  }, [favouriteMeals]);

  return {
    favouriteMealsResponse: data,
    favouriteMeals,
    favouriteMealsByMealTime,
    favouriteMealsSet,
    isFavouriteLoading: isLoading,
    isRefetching,
    isError,
    error,
    refetch,
  };
};
